import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Check, Sparkles, Cog, Clock, Bell } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FRECUENCIAS = ['Diario', 'Cada vez que aparezca', '3 veces por semana', 'Cuando lo necesite'];

export default function Modulo45Automatizacion() {
    const [disparador, setDisparador] = useState('');
    const [etiqueta, setEtiqueta] = useState('');
    const [accion, setAccion] = useState('');
    const [frecuencia, setFrecuencia] = useState('');
    const [recordatorio, setRecordatorio] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');

    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/automatizacion`, { withCredentials: true });
                if (data.data) {
                    const d = data.data;
                    if (d.disparador) setDisparador(d.disparador);
                    if (d.etiqueta) setEtiqueta(d.etiqueta);
                    if (d.accion) setAccion(d.accion);
                    if (d.frecuencia) setFrecuencia(d.frecuencia);
                    if (d.recordatorio) setRecordatorio(d.recordatorio);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        };
        loadDraft();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'automatizacion', data: { disparador, etiqueta, accion, frecuencia, recordatorio } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [disparador, etiqueta, accion, frecuencia, recordatorio]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [disparador, etiqueta, accion, frecuencia, recordatorio, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (disparador.trim().length > 0 && etiqueta.trim().length > 0 && accion.trim().length > 0 && frecuencia.length > 0 && recordatorio.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) { toast.error('Completa todos los campos del plan'); return; }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/automatizacion/submit`, {
                disparador_microsenal: disparador,
                etiqueta_nombre: etiqueta,
                accion_elegida: accion,
                frecuencia,
                recordatorio
            }, { withCredentials: true });
            setAiInsight(data.ai_insight); setSubmitted(true); toast.success('Capítulo 4 completado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); } finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Cog className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Plan de intervención creado</h2>
                    <p className="text-[#5FAF9D] font-medium mb-4">Tu secuencia SI-ENTONCES está lista</p>
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="automatizacion" chapter={4} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-automatizacion">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Cap. 4</span><span className="text-xs text-[#7A8A93]">4.5</span></div>
                                <h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Automatización de intervención</h1>
                            </div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#5FAF9D] to-[#4A9A89] p-4 sm:p-6 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center"><Cog className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-white text-base sm:text-lg">Diseña tu plan SI-ENTONCES</CardTitle><p className="text-xs sm:text-sm text-white/70">Convierte tu intervención en un hábito automático</p></div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-5">
                        {/* Disparador */}
                        <div className="space-y-2 p-4 bg-[#D07F7E]/5 rounded-xl border border-[#D07F7E]/20">
                            <Label className="text-sm font-semibold text-[#D07F7E]">SI detecto esta microseñal...</Label>
                            <p className="text-xs text-[#7A8A93]">Tu disparador: la primera señal que identificaste en módulos anteriores</p>
                            <Textarea value={disparador} onChange={e => setDisparador(e.target.value)} placeholder='Ej: Cuando siento tensión en el pecho al abrir el documento...' className="border-[#D07F7E]/20 bg-white focus:ring-[#D07F7E]/20 focus:border-[#D07F7E]/40 rounded-xl min-h-[70px]" data-testid="input-disparador" />
                        </div>

                        {/* Etiqueta */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">ENTONCES nombro lo que pasa</Label>
                            <p className="text-xs text-[#7A8A93]">La etiqueta que le das a tu estado interno</p>
                            <input type="text" value={etiqueta} onChange={e => setEtiqueta(e.target.value)} placeholder='Ej: "Es la resistencia, no soy yo"' className="w-full px-3 py-2.5 text-sm border border-[#DCE3E8] rounded-xl bg-[#F5F7F8] focus:ring-1 focus:ring-[#5FAF9D] outline-none" data-testid="input-etiqueta" />
                        </div>

                        {/* Acción elegida */}
                        <div className="space-y-2 p-4 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                            <Label className="text-sm font-semibold text-[#5FAF9D]">Y ejecuto esta acción</Label>
                            <p className="text-xs text-[#7A8A93]">La micro-acción concreta que realizas en lugar de evitar</p>
                            <Textarea value={accion} onChange={e => setAccion(e.target.value)} placeholder='Ej: Abro el documento y escribo una sola línea sin exigencia de calidad' className="border-[#5FAF9D]/20 bg-white focus:ring-[#5FAF9D]/20 focus:border-[#5FAF9D]/40 rounded-xl min-h-[70px]" data-testid="input-accion" />
                        </div>

                        {/* Frecuencia */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2"><Clock className="h-4 w-4" />Frecuencia</Label>
                            <div className="flex flex-wrap gap-2">
                                {FRECUENCIAS.map(f => (
                                    <button key={f} onClick={() => setFrecuencia(f)}
                                        className={`px-3 py-1.5 text-xs sm:text-sm rounded-xl transition-all ${frecuencia === f ? 'bg-[#3E5F6E] text-white shadow-md' : 'bg-[#F5F7F8] text-[#7A8A93] border border-[#DCE3E8]'}`}
                                        data-testid={`freq-${f.toLowerCase().replace(/\s/g, '-')}`}>{f}</button>
                                ))}
                            </div>
                        </div>

                        {/* Recordatorio */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2"><Bell className="h-4 w-4" />Recordatorio o ancla</Label>
                            <p className="text-xs text-[#7A8A93]">¿Cómo vas a recordar ejecutar el plan? Un post-it, alarma, frase...</p>
                            <input type="text" value={recordatorio} onChange={e => setRecordatorio(e.target.value)} placeholder='Ej: Post-it en el monitor: "Pausa, nombra, actúa"' className="w-full px-3 py-2.5 text-sm border border-[#DCE3E8] rounded-xl bg-[#F5F7F8] focus:ring-1 focus:ring-[#5FAF9D] outline-none" data-testid="input-recordatorio" />
                        </div>

                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-automatizacion">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Completar capítulo</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
